import {ensureArray} from '@amblique/king-living/app/utils/utils';
import {FALLBACK_SHOP_TAB_ID} from '@amblique/king-living/app/constants/navigation-drawer';
import {SHOWROOMS_LISTING_PATH} from '@amblique/king-living/app/constants/site-config';
import {isAbsoluteURL} from '@salesforce/retail-react-app/app/page-designer/utils';

const trimHref = (value) => (typeof value === 'string' ? value.trim() : '');

/**
 * Read a Contentful collection or a plain array.
 *
 * @param {unknown} value
 * @returns {Array<Object>}
 */
export const getCollectionItems = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  return ensureArray(value?.items).filter(Boolean);
};

/** First non-empty Contentful collection (skip `{items: []}` so cursor can win). */
const firstCollectionItems = (...values) => {
  for (const value of values) {
    const items = getCollectionItems(value);
    if (items.length) {
      return items;
    }
  }

  return [];
};

/**
 * @param {Object|null|undefined} link
 * @returns {string}
 */
export const getLinkUrl = (link) => {
  const url = trimHref(link?.url);

  if (url) {
    return url;
  }

  const href = trimHref(link?.href);

  if (href) {
    return href;
  }

  const action = trimHref(link?.action);

  if (action.startsWith('/') || action.startsWith('http')) {
    return action;
  }

  return '';
};

/**
 * CMS Link routing: storefront paths use SPA `to`; http(s), //, hash, or Open in new tab use `href`.
 *
 * @param {Object|null|undefined} link
 * @param {string} [href]
 * @returns {{to?: string, href?: string, target?: string, rel?: string}}
 */
export const getNavLinkAnchorProps = (link, href = getLinkUrl(link)) => {
  const trimmed = trimHref(href);

  if (!trimmed) {
    return {};
  }

  const openNewTab = Boolean(link?.openInNewTab);
  const useHref = openNewTab
    || trimmed.startsWith('#')
    || isAbsoluteURL(trimmed);

  if (useHref) {
    return {
      href: trimmed,
      ...(openNewTab ? {target: '_blank', rel: 'noopener noreferrer'} : {}),
    };
  }

  return {to: trimmed};
};

/**
 * @param {Object|null|undefined} link
 * @returns {string}
 */
export const getLinkText = (link) => (
  link?.linkText || link?.text || link?.title || ''
);

const GUEST_FOOTER_ACTIONS = new Set(['guestbtn', 'guest', 'login', 'signup', 'sign-up', 'sign_up']);
const CUSTOMER_FOOTER_ACTIONS = new Set(['customerbtn', 'customer', 'account', 'myaccount', 'my_account']);
const LOGOUT_FOOTER_ACTIONS = new Set(['logout', 'signout', 'sign-out', 'log-out']);
const SHOWROOM_FOOTER_ACTIONS = new Set([
  'showroom',
  'showroombtn',
  'store',
  'storebtn',
  'findshowroom',
  'find_showroom',
  'find-showroom',
  'find a showroom',
  'find_a_showroom',
  'find-a-showroom',
  'findastore',
  'find_a_store',
  'find-a-store',
  'find a store',
  'myshowroom',
  'my_showroom',
  'my showroom',
  'location',
  'locationbtn',
  'showrooms',
]);

/**
 * Contentful Link.action (enum), lowercased.
 *
 * @param {Object|null|undefined} link
 * @returns {string}
 */
export const getLinkAction = (link) => String(link?.action || '').trim().toLowerCase();

const UTILITY_LINK_TYPE_VARIANTS = {
  primary: 'filled-light',
  secondary: 'outlined-light',
  card: 'base',
  'filled dark': 'filled-dark',
  'outlined dark': 'outlined-dark',
  'filled light': 'filled-light',
  'outlined light': 'outlined-light',
  filleddark: 'filled-dark',
  outlineddark: 'outlined-dark',
  filledlight: 'filled-light',
  outlinedlight: 'outlined-light',
};

/**
 * Contentful Link.type → BaseLink variant (bg/color). Empty or Card → not a typed button.
 *
 * @param {Object|null|undefined} link
 * @returns {string}
 */
export const getUtilityLinkButtonVariant = (link) => {
  const type = String(link?.linkType || link?.type || '').trim().toLowerCase();

  if (!type) {
    return '';
  }

  return UTILITY_LINK_TYPE_VARIANTS[type.replace(/\s+/g, ' ')]
    || UTILITY_LINK_TYPE_VARIANTS[type.replace(/\s/g, '')]
    || '';
};

/**
 * Extra utility row that should render as a DS button (not plain text).
 *
 * @param {Object|null|undefined} link
 * @returns {boolean}
 */
export const isTypedUtilityButton = (link) => {
  const variant = getUtilityLinkButtonVariant(link);

  return Boolean(variant) && variant !== 'base';
};

/**
 * @param {Object|null|undefined} link
 * @returns {boolean}
 */
export const isGuestAccountAction = (link) => GUEST_FOOTER_ACTIONS.has(getLinkAction(link));

/**
 * @param {Object|null|undefined} link
 * @returns {boolean}
 */
export const isCustomerAccountAction = (link) => CUSTOMER_FOOTER_ACTIONS.has(getLinkAction(link));

/**
 * @param {Object|null|undefined} link
 * @returns {boolean}
 */
export const isLogoutAction = (link) => LOGOUT_FOOTER_ACTIONS.has(getLinkAction(link));

/**
 * Find a showroom pill. Click is owned by FE (cookie slug → detail, else listing).
 *
 * @param {Object|null|undefined} link
 * @returns {boolean}
 */
export const isShowroomAction = (link) => SHOWROOM_FOOTER_ACTIONS.has(getLinkAction(link));

const getLinkPath = (link) => {
  const href = getLinkUrl(link);

  if (!href) {
    return '';
  }

  try {
    const url = href.startsWith('http') ? new URL(href) : new URL(href, 'http://local.invalid');

    return url.pathname.replace(/\/+$/, '') || '/';
  } catch (_error) {
    return href.split('?')[0].replace(/\/+$/, '');
  }
};

const isShowroomHref = (link) => {
  const path = getLinkPath(link);

  return path === SHOWROOMS_LISTING_PATH
    || path.endsWith(SHOWROOMS_LISTING_PATH)
    || /\/showroom\//.test(path);
};

/**
 * Showroom pill: Contentful `action` or a showrooms URL.
 *
 * @param {Object|null|undefined} link
 * @returns {boolean}
 */
export const isShowroomFooterLink = (link) => isShowroomAction(link) || isShowroomHref(link);

export const MY_ACCOUNT_FOOTER_ID = 'mega-menu-mobile-my-account';
export const SIGN_OUT_FOOTER_ID = 'mega-menu-mobile-sign-out';

/**
 * Logged-in footer: FE owns My account + Sign out. CMS CustomerBtn / logout rows are ignored.
 *
 * @param {Array<Object>} links already auth-filtered, without CMS account/logout
 * @param {{isRegistered: boolean, accountLabel: string, signOutLabel: string}} options
 * @returns {Array<Object>}
 */
export const injectLoggedInAccountAndSignOut = (links, {isRegistered, accountLabel, signOutLabel} = {}) => {
  const items = Array.isArray(links) ? links : [];

  if (!isRegistered) {
    return items;
  }

  return [
    {
      sys: {id: MY_ACCOUNT_FOOTER_ID},
      action: 'account',
      linkText: accountLabel,
    },
    ...items,
    {
      sys: {id: SIGN_OUT_FOOTER_ID},
      action: 'logout',
      linkText: signOutLabel,
    },
  ];
};

/**
 * Footer CTA whose click is owned by FE (login / account / logout).
 * Guest copy is i18n. Logged-in My account + Sign out are hardcoded (not CMS CustomerBtn / logout).
 *
 * @param {Object|null|undefined} link
 * @returns {boolean}
 */
export const isAccountFooterAction = (link) => (
  isGuestAccountAction(link) || isCustomerAccountAction(link) || isLogoutAction(link)
);

/**
 * Keep a footer row when it has copy, a known action (copy can fall back), or a URL.
 *
 * @param {Object|null|undefined} link
 * @returns {boolean}
 */
export const hasRenderableFooterLink = (link) => {
  if (isAccountFooterAction(link) || isShowroomAction(link)) {
    return true;
  }

  return Boolean(getLinkText(link) && getLinkUrl(link));
};

/**
 * Auth-visibility for mobile drawer footer / utility pills.
 *
 * @param {Object|null|undefined} link
 * @param {boolean} isRegistered
 * @returns {boolean}
 */
export const isFooterLinkVisible = (link, isRegistered) => {
  if (isGuestAccountAction(link)) {
    return !isRegistered;
  }

  if (isCustomerAccountAction(link) || isLogoutAction(link)) {
    return isRegistered;
  }

  return hasRenderableFooterLink(link);
};

/**
 * @param {Object|null|undefined} source
 * @returns {Array<Object>}
 */
export const getPromotionalLinks = (source) => firstCollectionItems(
  source?.promotionalLinksCollection,
  source?.promotionalLinksCursorCollection,
  source?.promotionalLinks
);

/**
 * @param {Object|null|undefined} menuItem
 * @returns {string}
 */
export const getMenuItemId = (menuItem) => menuItem?.sys?.id || '';

/**
 * @param {Object|null|undefined} menuItem
 * @returns {string}
 */
export const getMenuItemTitle = (menuItem) => menuItem?.title || '';

/**
 * @param {Object|null|undefined} menuItem
 * @returns {Array<Object>}
 */
export const getMenuItemImages = (menuItem) => firstCollectionItems(
  menuItem?.imagesCollection,
  menuItem?.imagesCursorCollection,
  menuItem?.images
);

/**
 * @param {Object|null|undefined} menuItem
 * @returns {Array<Object>}
 */
export const getMenuItemColumns = (menuItem) => firstCollectionItems(
  menuItem?.submenuCollection,
  menuItem?.submenuCursorCollection,
  menuItem?.submenu
);

/**
 * @param {Object|null|undefined} column
 * @returns {string}
 */
export const getColumnTitle = (column) => (
  column?.headerText || column?.title || ''
);

/**
 * NavigationColumn links live on `linksCollection`. L1 MenuItem queries only
 * hydrate BaseNavigationColumn (title/header), so this is often empty until
 * the column is fetched by id.
 *
 * @param {Object|null|undefined} column
 * @returns {Array<Object>}
 */
export const getColumnLinks = (column) => firstCollectionItems(
  column?.linksCollection,
  column?.linksCursorCollection,
  column?.links
);

/**
 * @param {Object|null|undefined} menuItem
 * @returns {Object|null}
 */
export const getFeaturedLink = (menuItem) => menuItem?.featuredLink || menuItem?.link || null;

/**
 * Shop all label is always i18n “Shop all {category}”, not CMS featuredLink.text.
 *
 * @param {Object|null|undefined} menuItem
 * @param {function} formatMessage
 * @returns {string}
 */
export const formatShopAllLabel = (menuItem, formatMessage) => {
  const title = getMenuItemTitle(menuItem);
  const category = title ? title.toLowerCase() : '';

  return formatMessage({
    defaultMessage: 'Shop all {category}',
    id: 'mega_menu.shop_all_category',
  }, {category});
};

/**
 * Desktop L3 tiles on a Column 2 Link (`Menu Image Gallery` / menuImageGallery).
 * Not the single `image` icon field (footer pills).
 *
 * @param {Object|null|undefined} link
 * @returns {Array<Object>}
 */
export const getLinkMenuGallery = (link) => firstCollectionItems(
  link?.menuImageGalleryCollection,
  link?.menuImageGalleryCursorCollection,
  link?.menuImageGallery
);

/**
 * @param {Object|null|undefined} menuItem
 * @returns {boolean}
 */
export const menuItemHasSubmenu = (menuItem) => getMenuItemColumns(menuItem).length > 0;

/**
 * Contentful Primary Items on the NavigationMenu (Shop / Discover / Support).
 * Empty means merch has not authored Header 2.0 tabs — use the legacy hover nav.
 *
 * @param {Object|null|undefined} navigationMenu
 * @returns {Array<Object>}
 */
export const getCmsPrimaryItems = (navigationMenu) => getCollectionItems(
  navigationMenu?.primaryItemsCollection
  || navigationMenu?.primaryItems
);

/**
 * @param {Object|null|undefined} navigationMenu
 * @returns {boolean}
 */
export const hasCmsPrimaryItems = (navigationMenu) => getCmsPrimaryItems(navigationMenu).length > 0;

/**
 * Header 1.0 (hover / legacy menuItems) unless CMS actually published Primary Items.
 * Missing nav, Header stub (`sys.id` only), empty collection, or unpublished
 * Primary Menu field → version 1. Do not wait for collection keys to exist.
 *
 * @param {Object|null|undefined} navigationMenu
 * @returns {boolean}
 */
export const isLegacyNavigationMenu = (navigationMenu) => !hasCmsPrimaryItems(navigationMenu);

/**
 * Map a NavigationMenu payload into Header 2.0 primary tabs.
 * Does not synthesize a Shop tab from `menuItemsCollection` — that list is
 * the legacy hover / mobile L1 fallback when Primary Items are unpublished.
 *
 * @param {Object|null|undefined} navigationMenu
 * @param {Object} [options]
 * @param {string} [options.fallbackTitle='Shop']
 * @returns {Array<{id: string, title: string, menuItems: Array, promotionalLinks: Array}>}
 */
export const mapNavigationPrimaryTabs = (navigationMenu, options = {}) => {
  const fallbackTitle = options.fallbackTitle || 'Shop';
  const menuPromotionalLinks = getPromotionalLinks(navigationMenu);
  const cmsPrimaryItems = getCmsPrimaryItems(navigationMenu);

  return cmsPrimaryItems.map((item) => {
    const itemPromotionalLinks = getPromotionalLinks(item);

    return {
      id: item?.sys?.id || item?.title || FALLBACK_SHOP_TAB_ID,
      title: item?.title || fallbackTitle,
      menuItems: getCollectionItems(
        item?.menuItemsCollection || item?.menuItems
      ),
      // Child Primary Item promo wins; empty → inherit NavigationMenu parent.
      promotionalLinks: itemPromotionalLinks.length ?
        itemPromotionalLinks :
        menuPromotionalLinks,
    };
  });
};

/**
 * Mobile footer links from Header.utilityLinks.
 *
 * @param {Object|null|undefined} header
 * @returns {Array<Object>}
 */
export const getUtilityLinks = (header) => getCollectionItems(
  header?.utilityLinksCollection || header?.utilityLinks
);

/**
 * Mobile drawer footer from Header.utilityLinks only.
 * Logged-in My account + Sign out are hardcoded (no CMS CustomerBtn / logout).
 * Extra CMS links with Type Primary/Secondary/Filled Dark/Outlined Dark use
 * BaseLink filled/outlined variants (header height/spacing; colors from Type).
 *
 * @param {{utilityLinks?: Array, isRegistered?: boolean, accountLabel?: string, signOutLabel?: string}} options
 * @returns {Array<Object>}
 */
export const buildMobileDrawerFooterLinks = ({
  utilityLinks = [],
  isRegistered,
  accountLabel,
  signOutLabel,
} = {}) => {
  const cmsLinks = getCollectionItems(utilityLinks).filter((link) => (
    !isCustomerAccountAction(link) && !isLogoutAction(link)
  ));

  return injectLoggedInAccountAndSignOut(
    cmsLinks.filter((link) => isFooterLinkVisible(link, isRegistered)),
    {isRegistered, accountLabel, signOutLabel}
  );
};
